/**
 * MaintActionsLanding — Wave 6.3 thin list view of MaintActions in flight or
 * pending close-out.
 *
 * Reads `client.retail.maintActions.listMy({ status: 'open' })`. Each row is a
 * compact card with the customer + action label and a kebab "Close action…"
 * affordance that opens CloseMaintActionModal.
 *
 * This surface is intentionally minimal — it exposes the close-out
 * affordance promised by the Wave 6.3 brief without trying to replace the
 * RAM wizard's per-action launches. RAM remains the entry point for
 * starting a maintenance action; this list is where you finish it.
 */

import { useEffect, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { Banner, Pill } from '@/components/primitives';
import { TestModeBadge } from '@/modules/_shared/TestModeBadge';
import { useApi } from '@/modules/RAO/hooks/useApi';
import type {
  CartResolutionCode,
  MaintActionResolutionDto,
} from '@retail-ops/shared-types';
import { CloseMaintActionModal } from './CloseMaintActionModal';

export function MaintActionsLanding(): JSX.Element {
  const { client, makeSignal } = useApi();
  const [rows, setRows] = useState<ReadonlyArray<MaintActionResolutionDto>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [closeTarget, setCloseTarget] = useState<MaintActionResolutionDto | null>(null);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const res = await client.retail.maintActions.listMy({ status: 'open' }, { signal: makeSignal() });
      const anyRes = res as unknown as { success?: boolean; data?: MaintActionResolutionDto[]; error?: string };
      if (anyRes.success === false) {
        setError(anyRes.error ?? 'Failed to load maintenance actions.');
        return;
      }
      setRows(anyRes.data ?? []);
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'Failed to load maintenance actions.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleResolved(resolved: MaintActionResolutionDto, _code: CartResolutionCode): void {
    // Optimistic remove from the list.
    setRows((prev) => prev.filter((r) => r.maintActionId !== resolved.maintActionId));
  }

  return (
    <div style={{ padding: 'var(--sp-7)' }}>
      <header style={{ marginBottom: 'var(--sp-6)' }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Open maintenance actions</h1>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-700)' }}>
          Maintenance items waiting on close-out. Use the kebab menu to record the resolution
          (Processed / Withdrawn / Adverse Action / Cancelled / System-Error).
        </p>
      </header>

      {error && <Banner tone="err">{error}</Banner>}
      {loading && <div style={{ fontSize: 13, color: 'var(--ink-700)' }}>Loading…</div>}

      {!loading && rows.length === 0 && (
        <div
          style={{
            padding: 'var(--sp-8)',
            background: 'var(--surface-card)',
            border: '1px dashed var(--ink-300)',
            borderRadius: 'var(--r-md)',
            color: 'var(--ink-700)',
            textAlign: 'center',
          }}
        >
          No open maintenance actions.
        </div>
      )}

      <div style={{ display: 'grid', gap: 'var(--sp-4)', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
        {rows.map((r) => (
          <MaintActionCard key={r.maintActionId} action={r} onClose={() => setCloseTarget(r)} />
        ))}
      </div>

      {closeTarget && (
        <CloseMaintActionModal
          open
          action={closeTarget}
          onClose={() => setCloseTarget(null)}
          onResolved={(resolved, code) => {
            handleResolved(resolved, code);
            setCloseTarget(null);
          }}
        />
      )}
    </div>
  );
}

function MaintActionCard({
  action,
  onClose,
}: {
  action: MaintActionResolutionDto;
  onClose: () => void;
}): JSX.Element {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  return (
    <div
      style={{
        position: 'relative',
        padding: 'var(--sp-5)',
        background: 'var(--surface-card)',
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <button
        type="button"
        aria-label="Action menu"
        onClick={() => setMenuOpen((v) => !v)}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--ink-600)',
        }}
      >
        <MoreVertical size={16} aria-hidden />
      </button>
      {menuOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 32,
            right: 8,
            background: 'var(--surface-card)',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            zIndex: 20,
            minWidth: 160,
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              onClose();
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: 'var(--sp-3) var(--sp-4)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              color: 'var(--ink-900)',
            }}
          >
            Close action…
          </button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-3)' }}>
        <Pill variant="muted">{action.actionCode}</Pill>
        {action.isTestMode && <TestModeBadge active size="sm" />}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink-900)' }}>
        {action.displayLabel}
      </div>
      {action.customerName && (
        <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 4 }}>
          {action.customerName}
        </div>
      )}
      <div style={{ fontSize: 12, color: 'var(--ink-500)', marginTop: 'var(--sp-3)' }}>
        {action.branchName ?? action.branchCode ?? '—'} ·{' '}
        {action.officerDisplayName ?? action.officerCode ?? '—'}
      </div>
    </div>
  );
}
